import React from 'react';

import { ButtonBase, Collapse, Typography, useTheme } from '@mui/material';
import { capitalCase } from 'change-case';
import { CancelCircleIcon } from 'src/components/icons/CancelCircleIcon';
import { ChatChevronIcon } from 'src/components/icons/ChatChevronIcon';
import { CheckCircleIcon } from 'src/components/icons/CheckCircleIcon';
import { ErrorIcon } from 'src/components/icons/ErrorIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { RadioUnselectedIcon } from 'src/components/icons/RadioUnselectedIcon';
import { makeStyles } from 'tss-react/mui';

import { DrafterNodeProvider } from './DrafterNodeContext';
import type { SubAgentRow } from './useSubAgentRows';
import type { ChatAssistantMessage } from '../../../chat-types';
import { ChatPanelMarkdown } from '../../../ChatPanelMarkdown/ChatPanelMarkdown';
import type { ChatDocument } from '../../ChatPanelAssistantMessage';

const STATUS_LABELS = {
  awaitingInput: 'Waiting for you…',
  cancelled: 'Cancelled',
  completed: 'Completed',
  failed: 'Failed',
  queued: 'Queued',
  started: 'In progress…',
} as const satisfies Record<SubAgentRow['status'], string>;

const STATUS_ICONS = {
  // Paused for the user: keep the active spinner so the node reads in-progress.
  awaitingInput: () => <PulseIcon variant={PulseIconVariant.LoadingPrimary} />,
  // Neutral grey circle-X: a user-initiated Stop is not an error.
  cancelled: ({ neutralFill }: StatusIconProps) => <CancelCircleIcon fill={neutralFill} />,
  completed: ({ successFill }: StatusIconProps) => <CheckCircleIcon fill={successFill} />,
  failed: () => <ErrorIcon sx={{ fontSize: 18 }} />,
  queued: ({ neutralFill }: StatusIconProps) => <RadioUnselectedIcon fill={neutralFill} />,
  started: () => <PulseIcon variant={PulseIconVariant.LoadingPrimary} />,
} satisfies Record<SubAgentRow['status'], (props: StatusIconProps) => JSX.Element>;

const useStyles = makeStyles()(theme => ({
  body: {
    padding: theme.spacing(0, 1.5, 1, 4.5),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
  },
  chevron: {
    flexShrink: 0,
    transition: 'transform 150ms ease',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
    width: '100%',
  },
  status: {
    color: theme.palette.text.secondary,
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  titleColumn: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
}));

export const SubAgentCard = React.memo(function SubAgentCard({
  documentInView,
  expanded,
  message,
  onToggle,
  row,
  threadId,
}: {
  documentInView: ChatDocument | null;
  expanded: boolean;
  message: ChatAssistantMessage;
  onToggle: () => void;
  row: SubAgentRow;
  threadId: UUID;
}) {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const hasBody = row.nodeText.length > 0;

  return (
    <div className={classes.card}>
      <ButtonBase
        focusRipple
        aria-expanded={expanded}
        className={classes.header}
        disabled={!hasBody}
        onClick={onToggle}
      >
        <StatusIcon
          neutralFill={theme.palette.text.secondary}
          status={row.status}
          successFill={theme.palette.success.main}
        />
        <div className={classes.titleColumn}>
          <Typography className={classes.title} variant="body2">
            {row.title ?? capitalCase(row.agent)}
          </Typography>
          <Typography className={classes.status} variant="caption">
            {STATUS_LABELS[row.status]}
          </Typography>
        </div>
        {hasBody && (
          <ChatChevronIcon className={cx(classes.chevron, expanded && classes.chevronOpen)} />
        )}
      </ButtonBase>
      {/**
       * MUI `Collapse` measures child height and animates it; matches
       * the slow open/close motion used by the "Thought process" block
       * (`CodeThinkingElement`) and the suggestion-list rows. `unmountOnExit`
       * keeps the markdown subtree (which can be heavy) out of the React
       * tree entirely while collapsed, so streaming updates to other
       * cards don't pay the cost of re-rendering hidden bodies.
       */}
      <Collapse unmountOnExit in={expanded && hasBody} timeout="auto">
        <div className={classes.body}>
          <DrafterNodeProvider
            value={row.agent === 'drafter' ? { nodeId: row.nodeId, threadId } : null}
          >
            <ChatPanelMarkdown
              completionText={row.nodeText}
              documentInView={documentInView}
              message={message}
              threadId={threadId}
            />
          </DrafterNodeProvider>
        </div>
      </Collapse>
    </div>
  );
});

interface StatusIconProps {
  neutralFill: string;
  status: SubAgentRow['status'];
  successFill: string;
}

function StatusIcon({ neutralFill, status, successFill }: StatusIconProps) {
  const Icon = STATUS_ICONS[status];
  return <Icon neutralFill={neutralFill} status={status} successFill={successFill} />;
}
